import { format } from "date-fns";
import { useMemo, Fragment } from "react";
import { Button, Icon, Typography, Tooltip } from "@/components/atoms";
import { Loading } from "@/components/atoms/Loading/Loading";
import { getDisabledPlanInfo } from "@/redux/apis/subscription/subscriptionLimits";
import { useGetSubscriptionPlansQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { ScheduledPlanChange, SubscriptionPlan } from "@/redux/apis/subscription/types";
import { clsx } from "@/utils";
import { FeatureCell } from "./FeatureCell";
import { PlanHeaderCell } from "./PlanHeaderCell";
import { transformSubscriptionPlans, transformFeatureSections } from "./helpers";

interface ComparePlansProps extends React.HTMLAttributes<HTMLDivElement> {
  currentPlan?: string; // Now accepts subscription ID instead of plan type
  scheduledPlanChange?: ScheduledPlanChange;
  onDowngrade?: (subscriptionId: string) => void;
  onUpgrade?: (subscriptionId: string) => void;
  onSubscribe?: (subscriptionId: string) => void;
  onReactivate?: (subscriptionId: string) => void;
  onCancelScheduledChange?: () => void;
  onViewDemo?: (plan: SubscriptionPlan) => void;
  isChangingPlan?: boolean;
  hasNoSubscription?: boolean;
  isPendingCancellation?: boolean;
  hideActions?: boolean;
}

export const ComparePlans = ({
  className,
  currentPlan,
  scheduledPlanChange,
  onDowngrade,
  onUpgrade,
  onSubscribe,
  onReactivate,
  onCancelScheduledChange,
  onViewDemo: _onViewDemo, // TODO: Uncomment usage when View Demo API is available
  isChangingPlan = false,
  hasNoSubscription = false,
  isPendingCancellation = false,
  hideActions = false,
  ...props
}: ComparePlansProps) => {
  const { data: subscriptionData, isLoading, error } = useGetSubscriptionPlansQuery();

  // Transform API data to component format
  const planConfigs = useMemo(() => {
    return subscriptionData ? transformSubscriptionPlans(subscriptionData) : [];
  }, [subscriptionData]);

  const featureSections = useMemo(() => {
    if (!subscriptionData) return [];
    const subscriptionIds = subscriptionData.subscriptions.map((s) => s.subscriptionId);
    return transformFeatureSections(subscriptionData, subscriptionIds);
  }, [subscriptionData]);

  // Calculate cumulative row index for alternating backgrounds
  let cumulativeRowIndex = 0;

  if (isLoading) {
    return (
      <div className={clsx("relative min-h-[400px] text-neutral-dark-gray", className)} {...props}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx("flex min-h-[400px] flex-col items-center justify-center text-neutral-dark-gray", className)} {...props}>
        <Typography variant="h3" className="text-error-primary">
          Error loading subscription plans
        </Typography>
        <Typography variant="paragraph" className="mt-2">
          Please try again later
        </Typography>
      </div>
    );
  }

  return (
    <div className={clsx("flex flex-col text-neutral-dark-gray", className)} {...props}>
      <div className="flex flex-col text-center">
        <Typography variant="title" className="text-neutral-silver-gray">
          Compare Plans
        </Typography>
        <Typography variant="h2">Compare our plans and find yours</Typography>
        <Typography variant="paragraph">Smart pricing that adapts to your needs, pay only for the usage that matters</Typography>
      </div>

      {/* Desktop View - Original 4-column layout */}
      <div className="mt-10 hidden w-full lg:flex">
        <div className="w-full pt-6">
          <div className="rounded-lg border border-neutral-mid-gray">
            <table className="w-full table-fixed">
              <thead>
                <tr className="bg-neutral-surface-gray">
                  <th className="w-[300px] rounded-tl-lg px-2.5 py-2 text-left align-top font-normal">
                    <Typography variant="h3">Features</Typography>
                  </th>
                  {planConfigs.map((plan, index) => (
                    <PlanHeaderCell
                      key={plan.id}
                      plan={plan}
                      isCurrentPlan={currentPlan === plan.id}
                      isLast={index === planConfigs.length - 1}
                      isDesktop={true}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureSections.map((section, sectionIndex) => (
                  <Fragment key={`section-${sectionIndex}`}>
                    <tr>
                      <td className="bg-white px-2.5 py-2">
                        <Typography variant="action" className="text-primary">
                          {section.title}
                        </Typography>
                      </td>
                      {planConfigs.map((plan) => (
                        <td key={plan.id} className={clsx("px-2.5 py-2", currentPlan === plan.id ? "bg-primary-tint-light" : "bg-white")} />
                      ))}
                    </tr>
                    {section.features.map((feature, featureIndex) => {
                      const isOddRow = cumulativeRowIndex % 2 === 0;
                      cumulativeRowIndex++;

                      return (
                        <tr key={`feature-${sectionIndex}-${featureIndex}`}>
                          <td className={clsx("px-2.5 py-2", isOddRow ? "bg-neutral-gray" : "bg-white")}>
                            <div className="flex flex-row items-center gap-2">
                              <Typography variant="paragraph" className="text-neutral-dark-gray">
                                {feature.name}
                              </Typography>
                              {feature.hasInfo && (
                                <Tooltip content={feature.description || "No description available"} delay={0}>
                                  <span className="inline-flex">
                                    <Icon
                                      isCustom
                                      name="help-circle"
                                      variant="Outline"
                                      size="md"
                                      className="flex-shrink-0 text-neutral-silver-gray"
                                    />
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          </td>
                          {planConfigs.map((plan) => (
                            <FeatureCell
                              key={plan.id}
                              value={feature.values[plan.id]}
                              isCurrentPlan={currentPlan === plan.id}
                              isOddRow={isOddRow}
                            />
                          ))}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
                {!hideActions && (
                  <tr>
                    <td className="rounded-bl-lg bg-white px-2.5 py-2"></td>
                    {planConfigs.map((plan, index) => {
                      const isScheduledPlan = scheduledPlanChange?.newSubscriptionId === plan.id;
                      const isCurrentPlan = currentPlan === plan.id;
                      const subscription = subscriptionData?.subscriptions.find((s) => s.subscriptionId === plan.id);
                      const disabledPlanInfo = subscription ? getDisabledPlanInfo(subscription.slug) : { disabled: false };
                      const isPlanDisabled = disabledPlanInfo.disabled;
                      // Block downgrades while the subscription is scheduled to
                      // cancel — reactivate first (mirrors the backend guard).
                      const currentPlanConfigForRow = planConfigs.find((p) => p.id === currentPlan);
                      const blockDowngrade =
                        !isCurrentPlan &&
                        isPendingCancellation &&
                        !!currentPlanConfigForRow &&
                        plan.fee < currentPlanConfigForRow.fee;

                      return (
                        <td
                          key={plan.id}
                          className={clsx(
                            "px-2.5 py-2",
                            isCurrentPlan ? "bg-primary-tint-light" : "bg-white",
                            index === planConfigs.length - 1 && "rounded-br-lg"
                          )}
                        >
                          <div className="flex flex-col gap-2">
                            {isPlanDisabled ? (
                              <Button size="sm" variant="primary" className="w-full" disabled>
                                {disabledPlanInfo.reason}
                              </Button>
                            ) : isScheduledPlan && scheduledPlanChange ? (
                              <>
                                <Button size="sm" variant="primary" className="w-full !border-info/30 !bg-info/10 !text-info" disabled>
                                  Scheduled {format(new Date(scheduledPlanChange.effectiveDate), "MMM d")}
                                </Button>
                                {onCancelScheduledChange && (
                                  <Button size="sm" variant="tertiary" className="w-full text-danger" onClick={onCancelScheduledChange}>
                                    Cancel
                                  </Button>
                                )}
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  className="w-full"
                                  disabled={(isCurrentPlan && !isPendingCancellation) || isChangingPlan || blockDowngrade}
                                  onClick={() => {
                                    if (isCurrentPlan && isPendingCancellation) {
                                      onReactivate?.(plan.id);
                                      return;
                                    }
                                    if (isCurrentPlan) return;
                                    if (hasNoSubscription) {
                                      onSubscribe?.(plan.id);
                                    } else {
                                      const currentPlanConfig = planConfigs.find((p) => p.id === currentPlan);
                                      if (currentPlanConfig && plan.fee < currentPlanConfig.fee) {
                                        onDowngrade?.(plan.id);
                                      } else {
                                        onUpgrade?.(plan.id);
                                      }
                                    }
                                  }}
                                >
                                  {isCurrentPlan && isPendingCancellation
                                    ? isChangingPlan
                                      ? "Re-activating..."
                                      : "Re-activate"
                                    : isCurrentPlan
                                    ? "Current Plan"
                                    : isChangingPlan
                                    ? hasNoSubscription
                                      ? "Subscribing..."
                                      : "Changing..."
                                    : hasNoSubscription
                                    ? "Subscribe"
                                    : (() => {
                                        const currentPlanConfig = planConfigs.find((p) => p.id === currentPlan);
                                        return currentPlanConfig && plan.fee < currentPlanConfig.fee ? "Downgrade Plan" : "Upgrade Plan";
                                      })()}
                                </Button>
                                {/* TODO: Uncomment when View Demo API is available */}
                                {/* <Button
                                size="sm"
                                variant="tertiary"
                                className="w-full"
                                onClick={() => {
                                  const subscription = subscriptionData?.subscriptions.find((s) => s.subscriptionId === plan.id);
                                  if (subscription) {
                                    onViewDemo?.(subscription);
                                  }
                                }}
                              >
                                View Demo
                              </Button> */}
                              </>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet View - 3-column layout with feature names as rows */}
      <div className="mt-10 flex w-full flex-col gap-4 lg:hidden">
        {/* Features Header */}
        <Typography variant="h3" className="text-left">
          Features
        </Typography>

        <div className="overflow-x-auto">
          <div className="rounded-lg border border-neutral-mid-gray">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-surface-gray">
                  {planConfigs.map((plan, index) => (
                    <PlanHeaderCell
                      key={plan.id}
                      plan={plan}
                      isCurrentPlan={currentPlan === plan.id}
                      isLast={index === planConfigs.length - 1}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureSections.map((section, sectionIndex) => {
                  let sectionRowIndex = 0;
                  return (
                    <Fragment key={`section-${sectionIndex}`}>
                      {/* Section Title Row */}
                      <tr>
                        {planConfigs.map((plan, index) => (
                          <td
                            key={plan.id}
                            className={clsx(
                              "px-2.5 py-2",
                              currentPlan === plan.id ? "bg-primary-tint-light" : "bg-white",
                              index === 0 && "text-left"
                            )}
                          >
                            {index === 0 && (
                              <Typography variant="action" className="text-primary">
                                {section.title}
                              </Typography>
                            )}
                          </td>
                        ))}
                      </tr>

                      {/* Feature Rows */}
                      {section.features.map((feature, featureIndex) => {
                        const isOddRow = sectionRowIndex % 2 === 0;
                        sectionRowIndex++;

                        return (
                          <Fragment key={`feature-${sectionIndex}-${featureIndex}`}>
                            {/* Feature Name Row */}
                            <tr>
                              {planConfigs.map((plan, index) => (
                                <td
                                  key={plan.id}
                                  colSpan={index === 0 ? planConfigs.length : undefined}
                                  className={clsx("px-2.5 py-2", isOddRow ? "bg-neutral-gray" : "bg-white", index > 0 && "hidden")}
                                >
                                  {index === 0 && (
                                    <div className="flex flex-row items-center gap-2">
                                      <Typography variant="paragraph" className="text-neutral-dark-gray">
                                        {feature.name}
                                      </Typography>
                                      {feature.hasInfo && (
                                        <Tooltip content={feature.description || "No description available"} delay={0}>
                                          <span className="inline-flex">
                                            <Icon
                                              isCustom
                                              name="help-circle"
                                              variant="Outline"
                                              size="md"
                                              className="flex-shrink-0 text-neutral-silver-gray"
                                            />
                                          </span>
                                        </Tooltip>
                                      )}
                                    </div>
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Feature Values Row */}
                            <tr>
                              {planConfigs.map((plan) => (
                                <FeatureCell
                                  key={plan.id}
                                  value={feature.values[plan.id]}
                                  isCurrentPlan={currentPlan === plan.id}
                                  isOddRow={isOddRow}
                                />
                              ))}
                            </tr>
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })}

                {/* Action Buttons Row */}
                {!hideActions && (
                  <tr>
                    {planConfigs.map((plan, index) => {
                      const isScheduledPlan = scheduledPlanChange?.newSubscriptionId === plan.id;
                      const isCurrentPlan = currentPlan === plan.id;
                      const subscription = subscriptionData?.subscriptions.find((s) => s.subscriptionId === plan.id);
                      const disabledPlanInfo = subscription ? getDisabledPlanInfo(subscription.slug) : { disabled: false };
                      const isPlanDisabled = disabledPlanInfo.disabled;
                      // Block downgrades while the subscription is scheduled to
                      // cancel — reactivate first (mirrors the backend guard).
                      const currentPlanConfigForRow = planConfigs.find((p) => p.id === currentPlan);
                      const blockDowngrade =
                        !isCurrentPlan &&
                        isPendingCancellation &&
                        !!currentPlanConfigForRow &&
                        plan.fee < currentPlanConfigForRow.fee;

                      return (
                        <td
                          key={plan.id}
                          className={clsx(
                            "px-2.5 py-2",
                            isCurrentPlan ? "bg-primary-tint-light" : "bg-white",
                            index === planConfigs.length - 1 && "rounded-br-lg",
                            index === 0 && "rounded-bl-lg"
                          )}
                        >
                          <div className="flex flex-col gap-2">
                            {isPlanDisabled ? (
                              <Button size="sm" variant="primary" className="w-full" disabled>
                                {disabledPlanInfo.reason}
                              </Button>
                            ) : isScheduledPlan && scheduledPlanChange ? (
                              <>
                                <Button size="sm" variant="primary" className="w-full !border-info/30 !bg-info/10 !text-info" disabled>
                                  Scheduled {format(new Date(scheduledPlanChange.effectiveDate), "MMM d")}
                                </Button>
                                {onCancelScheduledChange && (
                                  <Button size="sm" variant="tertiary" className="w-full text-danger" onClick={onCancelScheduledChange}>
                                    Cancel
                                  </Button>
                                )}
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  className="w-full"
                                  disabled={(isCurrentPlan && !isPendingCancellation) || isChangingPlan || blockDowngrade}
                                  onClick={() => {
                                    if (isCurrentPlan && isPendingCancellation) {
                                      onReactivate?.(plan.id);
                                      return;
                                    }
                                    if (isCurrentPlan) return;
                                    if (hasNoSubscription) {
                                      onSubscribe?.(plan.id);
                                    } else {
                                      const currentPlanConfig = planConfigs.find((p) => p.id === currentPlan);
                                      if (currentPlanConfig && plan.fee < currentPlanConfig.fee) {
                                        onDowngrade?.(plan.id);
                                      } else {
                                        onUpgrade?.(plan.id);
                                      }
                                    }
                                  }}
                                >
                                  {isCurrentPlan && isPendingCancellation
                                    ? isChangingPlan
                                      ? "Re-activating..."
                                      : "Re-activate"
                                    : isCurrentPlan
                                    ? "Current Plan"
                                    : isChangingPlan
                                    ? hasNoSubscription
                                      ? "Subscribing..."
                                      : "Changing..."
                                    : hasNoSubscription
                                    ? "Subscribe"
                                    : (() => {
                                        const currentPlanConfig = planConfigs.find((p) => p.id === currentPlan);
                                        return currentPlanConfig && plan.fee < currentPlanConfig.fee ? "Downgrade Plan" : "Upgrade Plan";
                                      })()}
                                </Button>
                                {/* TODO: Uncomment when View Demo API is available */}
                                {/* <Button
                                size="sm"
                                variant="tertiary"
                                className="w-full"
                                onClick={() => {
                                  const subscription = subscriptionData?.subscriptions.find((s) => s.subscriptionId === plan.id);
                                  if (subscription) {
                                    onViewDemo?.(subscription);
                                  }
                                }}
                              >
                                View Demo
                              </Button> */}
                              </>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
